import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Stack, Typography } from '@mui/material';

import BasicLoader from '../../components/Loaders/BasicLoader';
import TopicHeader from '../../components/Headers/TopicHeader';
import MobileHeader from '../../components/Mobile/Header';
import QuestionItem from './components/QuestionItem';

import { useAppSelector } from '../../hooks';
import {
  getSubTopicState,
  getGroupedSubTopics,
  getSubTopicById,
} from '../../store/SubTopicSlice';
import getQuestionsBySubTopic from '../../store/QuestionSlice/QuestionApi';
import { IQuestion, getQuestionState } from '../../store/QuestionSlice';
import { getMetaTagState } from '../../store/MetaTagSlice';

const QuestionsListView: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const location = useLocation();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const questionState = useAppSelector(getQuestionState);
  const metaTagState = useAppSelector(getMetaTagState);

  const { list: questionList, status: questionListStatus } = questionState;
  const { subTopic } = subTopicState;

  const paramSubTopic = params.subTopic || '';
  const paramSubTopicDivided = paramSubTopic.split('_').reverse();
  const subTopicId = paramSubTopicDivided[0] || '';

  const topicData = subTopic && subTopic.topic ? subTopic.topic : undefined;

  let curriculum = '';
  let subject = '';
  let subjectCode = '';
  let topicName = '';
  let subTopicName = '';
  const currentYear = new Date().getFullYear() || '';

  let metaTitle = `Tutorials with Questions and Answers ${currentYear}- Tutopiya`;

  let metaDesc = `Free Tutorials with revision questions and answers. Unravel the complexities with our curriculum and subject lessons in ${currentYear}.`;

  if (subTopic && topicData) {
    curriculum = topicData.curriculum || '';
    subject = topicData.subject || '';
    subjectCode = topicData.subjectCode || '';
    topicName = topicData.name || '';
    subTopicName = subTopic.name || '';

    if (curriculum && subject && subjectCode && topicName && subTopicName) {
      metaTitle = `${curriculum} (${subjectCode}) ${subTopicName} ${subject} Tutorials with Questions and Answers ${currentYear}- Tutopiya`;

      metaDesc = `Free Tutorials on ${subTopicName} in ${subject} with revision questions and answers. Unravel the complexities of ${topicName} with our ${curriculum} ${subjectCode} ${subject} lessons in ${currentYear}`;
    }
  }

  useEffect(() => {
    if (subTopicId) {
      dispatch(getQuestionsBySubTopic(subTopicId));
      dispatch(getGroupedSubTopics({ subTopicId }));
      dispatch(getSubTopicById(subTopicId));
    }
  }, [subTopicId]);

  return (
    <div>
      {!metaTagState.metaTags ||
        (!metaTagState.metaTags[location.pathname.replace(/[/]$/, '')] && (
          <Helmet>
            <title>{metaTitle}</title>
            <meta name="description" content={metaDesc} />
          </Helmet>
        ))}
      {questionList.length ? (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: getFAQStructuredData(questionList),
          }}
        />
      ) : null}
      <MobileHeader
        leftContents={{
          type: 'subtopic-nav-bar',
        }}
      />
      <TopicHeader
        subject={subject}
        topic={topicName}
        subTopics={[subTopicName]}
        isGetSubTopic={false}
      />
      {questionListStatus === 'loading' ? (
        <Stack
          justifyContent="center"
          style={{ minHeight: 'calc(100vh - 64px)' }}
        >
          <BasicLoader />
        </Stack>
      ) : (
        <Stack alignItems="center">
          {questionList.length ? (
            questionList.map((questionItem, index) => (
              <QuestionItem
                key={index}
                questionNo={index}
                questionData={questionItem}
              />
            ))
          ) : (
            <Typography
              color="secondary"
              variant="h6"
              fontWeight={600}
              textAlign="center"
              sx={{ m: '30px 0' }}
            >
              No questions are available for this subtopic.
            </Typography>
          )}
        </Stack>
      )}
    </div>
  );
};

const getFAQStructuredData = (questionList: IQuestion[]) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questionList.map((item) => ({
      '@type': 'Question',
      name: item.details,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answers.find((answerItem) => answerItem.isCorrect === true)
          ?.content,
      },
    })),
  };
  return JSON.stringify(schema);
};

export default QuestionsListView;
